/**
 * Axios instance with JWT refresh.
 *
 * A 403 is deliberately NOT treated as an auth failure: the backend answers 403
 * for out-of-scope entities (spec §21), and retrying or logging the user out
 * would hide a permissions problem behind a login screen.
 */
import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

import { useAuthStore } from "@/store/auth";

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? "/api/v1",
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshing: Promise<string | null> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const status = error.response?.status;

    if (status === 401 && original && !original._retried) {
      original._retried = true;
      refreshing ??= useAuthStore.getState().refresh();
      const token = await refreshing;
      refreshing = null;
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }
      useAuthStore.getState().logout();
    }
    return Promise.reject(error);
  },
);

export interface ApiError {
  error: string;
  detail?: string;
  entity?: string;
}

/**
 * DRF field errors, keyed by field name.
 *
 * Validation on these forms is genuinely per-field — an unresolvable employee
 * id and a too-short password are different problems with different fixes — so
 * a single banner would make the person hunt for which input to change.
 * `__all__` collects anything the backend did not attach to a field.
 */
export function fieldErrors(error: unknown): Record<string, string[]> {
  if (!axios.isAxiosError(error)) return {};
  const data = error.response?.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};

  const out: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (key === "error") continue;
    const messages = Array.isArray(value) ? value.map(String)
      : typeof value === "string" ? [value]
      : [];
    if (!messages.length) continue;
    out[key === "detail" || key === "non_field_errors" ? "__all__" : key] = messages;
  }
  return out;
}

export function describeError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as ApiError | undefined;
    if (payload?.error === "out_of_scope") {
      return payload.detail ?? "That record is outside your access.";
    }
    if (payload?.error === "assumption_unconfirmed") {
      return payload.detail ?? "This metric needs a business definition first.";
    }
    if (error.response?.status === 503) {
      return "A dependency is unavailable. Check the health endpoint.";
    }
    return payload?.detail ?? error.message;
  }
  return "Something went wrong.";
}
